import { useState } from 'react'
import { supabase } from '../lib/supabase'

export function usePresupuesto() {
  const [limites,     setLimites]     = useState({})
  const [isInherited, setIsInherited] = useState(false)
  const [currentAno,  setCurrentAno]  = useState(null)
  const [currentMes,  setCurrentMes]  = useState(null)
  const [loading,     setLoading]     = useState(false)
  const [saving,      setSaving]      = useState(false)
  const [error,       setError]       = useState(null)

  async function loadMes(ano, mes) {
    setLoading(true)
    setCurrentAno(ano)
    setCurrentMes(mes)

    const { data, error: fetchErr } = await supabase
      .from('presupuestos')
      .select('categoria, limite')
      .eq('ano', ano)
      .eq('mes', mes)

    if (fetchErr) { setError(fetchErr); setLoading(false); return }

    if (data && data.length > 0) {
      setLimites(data.reduce((acc, r) => ({ ...acc, [r.categoria]: r.limite }), {}))
      setIsInherited(false)
      setLoading(false)
      return
    }

    // Sin datos para este mes — buscar el mes anterior más reciente
    const { data: prev } = await supabase
      .from('presupuestos')
      .select('categoria, limite, ano, mes')
      .or(`ano.lt.${ano},and(ano.eq.${ano},mes.lt.${mes})`)
      .order('ano', { ascending: false })
      .order('mes', { ascending: false })

    if (!prev || prev.length === 0) {
      setLimites({})
      setIsInherited(false)
      setLoading(false)
      return
    }

    const prevKey = `${prev[0].ano}-${prev[0].mes}`
    const rowsToCopy = prev.filter(r => `${r.ano}-${r.mes}` === prevKey)
    const map = rowsToCopy.reduce((acc, r) => ({ ...acc, [r.categoria]: r.limite }), {})

    // Auto-copiar hacia el futuro (solo si el mes solicitado es posterior al actual)
    const now = new Date()
    const currentAnoNow = now.getFullYear()
    const currentMesNow = now.getMonth() + 1
    const isFuture = ano > currentAnoNow || (ano === currentAnoNow && mes > currentMesNow)

    if (isFuture) {
      const { data: { user } } = await supabase.auth.getUser()
      await supabase.from('presupuestos').insert(
        rowsToCopy.map(r => ({
          user_id: user.id,
          categoria: r.categoria,
          limite: r.limite,
          ano,
          mes,
        }))
      )
    }

    setLimites(map)
    setIsInherited(true)
    setLoading(false)
  }

  async function guardarLimite(categoria, limite) {
    setSaving(true)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      const { error: saveErr } = await supabase
        .from('presupuestos')
        .upsert(
          { user_id: user.id, categoria, limite: parseFloat(limite), ano: currentAno, mes: currentMes },
          { onConflict: 'user_id,categoria,ano,mes' }
        )
      if (!saveErr) {
        setLimites(prev => ({ ...prev, [categoria]: parseFloat(limite) }))
        setIsInherited(false)
      }
      return { error: saveErr }
    } finally {
      setSaving(false)
    }
  }

  async function eliminarLimite(categoria) {
    const { data: { user } } = await supabase.auth.getUser()
    const { error: delErr } = await supabase
      .from('presupuestos')
      .delete()
      .eq('user_id', user.id)
      .eq('categoria', categoria)
      .eq('ano', currentAno)
      .eq('mes', currentMes)

    if (!delErr) {
      setLimites(prev => {
        const next = { ...prev }
        delete next[categoria]
        return next
      })
    }
    return { error: delErr }
  }

  return { limites, isInherited, loading, saving, error, loadMes, guardarLimite, eliminarLimite }
}
